/* Reconciliation: the definitions this tenant has, and how each one's last run went. D30/D31.
 *
 * Built to the reference design, whose structure *is* the argument. The header carries the two
 * counts; a 2px rule closes it; the masking statement comes before any data, because a reader has to
 * know what a break is before reading a table of them; then search, the filters a steward actually
 * arrives with, and the definitions themselves — each row a link into its own runs.
 *
 * **The masking band is always shown, and it is the most important text on the screen.** Not because
 * the platform is proud of it — because a table headed "Key (masked)" two clicks away is otherwise
 * ambiguous between "we hid it from you" and "we never had it". The join runs inside the customer's
 * database with masking in the SELECT list (hard rule 4), so nothing on this screen or any screen
 * below it *can* recover a key.
 *
 * **The approver sees a sentence, not an error.** That role holds no reconciliation permission at
 * all, so every read here 403s — which is consistent rather than an oversight: every read it holds
 * pairs with an action it takes, and it takes no reconciliation action. Saying so is the difference
 * between a surface that belongs to somebody else and a surface that looks broken.
 *
 * **Two columns need data the list response does not carry, and both are bounded and disclosed.**
 * `tolerance_version` is null in list responses by contract, and a run's state lives on a different
 * route entirely — so Tolerances and Last run each need a per-definition read, capped by
 * `MAX_DETAIL_LOOKUPS`, past which the cells read "not read" rather than a figure nothing measured.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';

import { useConnections } from '@/entities/connection';
import {
  can,
  CREATE_RECONCILIATION_PERMISSION,
  READ_RECONCILIATION_PERMISSION,
  useIdentity,
} from '@/entities/session';
import { useDatasetNames } from '@/features/catalog-browser';
import type { Definition, Run } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { definitionQuery, runsQuery, useDefinitions } from '../api/reconciliation';
import { definitionPath } from '../model/paths';
import {
  BOARD_FILTERS,
  matchesBoardFilter,
  matchesSearch,
  toleranceSummary,
  type BoardFilter,
} from '../model/reconciliation';
import { NewDefinitionWizard } from './NewDefinitionWizard';
import { RunStateTag } from './RunStateTag';

const MAX_DETAIL_LOOKUPS = 12;

function ToleranceCell({
  definition,
}: {
  readonly definition: Definition | undefined;
}): ReactElement {
  const { t } = useTranslation();

  if (definition === undefined) {
    return <span className="lq-muted">{t('reconciliation.notRead')}</span>;
  }
  const summary = toleranceSummary(definition);
  if (summary === null) {
    return <span className="lq-muted">{t('reconciliation.notRead')}</span>;
  }

  return (
    <>
      {t('reconciliation.tolerance.summary', {
        count: summary.fieldCount,
        version: summary.version,
      })}
      <div className="lq-cell__secondary">
        {summary.byKind
          .map(([kind, count]) =>
            t('reconciliation.tolerance.mix', {
              count,
              kind: t(`reconciliation.kind.${kind}`),
            }),
          )
          .join(', ')}
      </div>
    </>
  );
}

function LastRunCell({
  runs,
}: {
  readonly runs: readonly Run[] | undefined;
}): ReactElement {
  const { t } = useTranslation();

  if (runs === undefined) {
    return <span className="lq-muted">{t('reconciliation.notRead')}</span>;
  }
  const last = runs[0];
  if (last === undefined) {
    return <span className="lq-muted">{t('reconciliation.neverRun')}</span>;
  }

  return (
    <>
      <RunStateTag state={last.state} />
      <div className="lq-cell__secondary">
        {t('reconciliation.lastRun.when', {
          when: new Date(last.requested_at).toLocaleString(),
          who: last.requested_by,
        })}
      </div>
    </>
  );
}

export function ReconciliationBoard(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const identity = useIdentity();
  const definitions = useDefinitions();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<BoardFilter>('all');
  const [wizardOpen, setWizardOpen] = useState(false);

  const mayRead = can(identity.data, READ_RECONCILIATION_PERMISSION);
  const mayAuthor = can(identity.data, CREATE_RECONCILIATION_PERMISSION);

  const all = definitions.isSuccess ? definitions.data.items : [];
  const looked = all.slice(0, MAX_DETAIL_LOOKUPS);

  const detailReads = useQueries({
    queries: looked.map((definition) => definitionQuery(definition.id)),
  });
  const runReads = useQueries({
    queries: looked.map((definition) => runsQuery(definition.id)),
  });

  /* Only ever used to populate the wizard's two pickers, and only fetched when it is open — a
     catalogue walk on every board paint would be two extra requests for a control most visits never
     touch. */
  const connections = useConnections();
  const datasetNames = useDatasetNames();

  const detailById = new Map<string, Definition>();
  const runsById = new Map<string, readonly Run[]>();
  looked.forEach((definition, index) => {
    const detail = detailReads[index];
    if (detail?.isSuccess === true) detailById.set(definition.id, detail.data);
    const runs = runReads[index];
    if (runs?.isSuccess === true) runsById.set(definition.id, runs.data.items);
  });

  const rows = all.filter(
    (definition) =>
      matchesSearch(definition, search) &&
      matchesBoardFilter(filter, runsById.get(definition.id)?.[0]),
  );

  const runsRead = runReads.every((read) => read.isSuccess);
  const runTotal = [...runsById.values()].reduce((sum, runs) => sum + runs.length, 0);

  if (identity.isSuccess && !mayRead) {
    return (
      <div className="lq-rc">
        <PageHeader
          title={t('reconciliation.title')}
          lede={t('reconciliation.description')}
        />
        <div className="lq-pagerule" />
        <EmptyState
          title={t('reconciliation.forbidden.title')}
          guidance={t('reconciliation.forbidden.body')}
        />
      </div>
    );
  }

  return (
    <div className="lq-rc">
      <PageHeader
        title={t('reconciliation.title')}
        lede={t('reconciliation.description')}
        meta={
          definitions.isSuccess ? (
            <>
              <div>{t('reconciliation.meta.definitions', { count: all.length })}</div>
              {runsRead && looked.length === all.length && (
                <div>{t('reconciliation.meta.runs', { count: runTotal })}</div>
              )}
            </>
          ) : undefined
        }
      />
      <div className="lq-pagerule" />

      <div className="lq-band lq-band--info">
        <div className="lq-band__title">{t('reconciliation.masked.title')}</div>
        <div className="lq-band__body">{t('reconciliation.masked.body')}</div>
      </div>

      <div className="lq-rc__toolbar">
        <input
          className="lq-rc__search"
          type="search"
          value={search}
          placeholder={t('reconciliation.search.placeholder')}
          aria-label={t('reconciliation.search.label')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <SegmentedFilter
          variant="ledger"
          label={t('reconciliation.filter.label')}
          value={filter}
          options={BOARD_FILTERS.map((each) => ({
            id: each,
            label: t(`reconciliation.filter.${each}`),
          }))}
          onChange={setFilter}
        />
        <div className="lq-rc__spacer" />
        {mayAuthor ? (
          <Button
            tone="primary"
            onClick={() => {
              setWizardOpen(true);
            }}
          >
            {t('reconciliation.newDefinition')}
          </Button>
        ) : (
          /* Offered and withheld read differently. An impossible action and an unpermitted one
             must not look alike. */
          <span className="lq-rc__gated" title={t('reconciliation.newDefinition.why')}>
            {t('reconciliation.newDefinition.gated')}
          </span>
        )}
      </div>

      {/* On the wire since the `page()` helper gained it, and discarded by this screen until the
          redesign: a capped list that looks complete is the defect, not the cap. */}
      {definitions.isSuccess && definitions.data.truncated && (
        <div className="lq-band lq-band--warn">
          <div className="lq-band__title">{t('reconciliation.truncated.title')}</div>
          <div className="lq-band__body">{t('reconciliation.truncated.board')}</div>
        </div>
      )}

      <SectionHead title={t('reconciliation.section.definitions')} />

      {definitions.isPending && <SkeletonRows rows={4} columns={6} />}

      {definitions.isError && (
        <Alert tone="error" title={t('reconciliation.loadFailed')}>
          {definitions.error.message}
        </Alert>
      )}

      {definitions.isSuccess &&
        (all.length === 0 ? (
          <EmptyState
            title={t('reconciliation.empty.title')}
            guidance={t('reconciliation.empty.body')}
          />
        ) : rows.length === 0 ? (
          /* A filter that matches nothing is not an empty estate, and saying so is what stops a
             reader concluding their definitions are gone. */
          <EmptyState
            title={t('reconciliation.noMatches.title')}
            guidance={t('reconciliation.noMatches.body')}
          />
        ) : (
          <DataTable
            variant="ledger"
            caption={t('reconciliation.caption')}
            columns={[
              { key: 'name', label: t('reconciliation.column.name') },
              { key: 'left', label: t('reconciliation.column.left') },
              { key: 'right', label: t('reconciliation.column.right') },
              { key: 'tolerances', label: t('reconciliation.column.tolerances') },
              { key: 'lastRun', label: t('reconciliation.column.lastRun') },
              { key: 'open', label: '', actions: true },
            ]}
          >
            {rows.map((definition) => (
              <tr
                key={definition.id}
                className="lq-rc__row"
                onClick={() => {
                  navigate(definitionPath(definition.id));
                }}
              >
                <td>
                  <strong className="lq-rc__name">{definition.name}</strong>
                  <div className="lq-cell__secondary">
                    {t('reconciliation.createdBy', {
                      who: definition.created_by,
                      when: new Date(definition.created_at).toLocaleDateString(),
                    })}
                  </div>
                </td>
                <td className="lq-rc__side lq-mono">
                  {definition.left.qualified_name}
                  <div className="lq-cell__secondary">
                    {t('reconciliation.keyColumns', {
                      columns: definition.left.key_columns.join(', '),
                    })}
                  </div>
                </td>
                <td className="lq-rc__side lq-mono">
                  {definition.right.qualified_name}
                  <div className="lq-cell__secondary">
                    {t('reconciliation.keyColumns', {
                      columns: definition.right.key_columns.join(', '),
                    })}
                  </div>
                </td>
                <td className="lq-cell--quiet">
                  <ToleranceCell definition={detailById.get(definition.id)} />
                </td>
                <td className="lq-nowrap">
                  <LastRunCell runs={runsById.get(definition.id)} />
                </td>
                <td className="lq-table__actions">
                  <Link
                    className="lq-rowaction"
                    to={definitionPath(definition.id)}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {t('reconciliation.showRuns')}
                  </Link>
                </td>
              </tr>
            ))}
          </DataTable>
        ))}

      {definitions.isSuccess && all.length > 0 && (
        <p className="lq-note">{t('reconciliation.definitions.foot')}</p>
      )}
      {all.length > MAX_DETAIL_LOOKUPS && (
        <p className="lq-note">
          {t('reconciliation.definitions.capped', {
            cap: MAX_DETAIL_LOOKUPS,
            total: all.length,
          })}
        </p>
      )}

      <NewDefinitionWizard
        open={wizardOpen}
        onClose={() => {
          setWizardOpen(false);
        }}
        connections={(connections.data?.items ?? []).map((connection) => ({
          id: connection.id,
          label: connection.name,
        }))}
        datasets={[...(datasetNames.data?.byId.values() ?? [])].sort()}
        existing={all}
      />
    </div>
  );
}
